/**
 * Bake-off output display (plan: docs/social-engine-plan.md, Section 4, 2026-09-27).
 * Turns a model's raw reply into labeled plain-text fields so every model's output
 * renders the same way: no raw JSON, and no whitespace, key-order or markdown
 * differences that could hint at which model wrote it. Bookkeeping fields are
 * flagged so the page can hide them behind "show all fields".
 */

export interface OutputField {
  key: string;      // stable path, e.g. "fun_facts.1.source"
  label: string;    // e.g. "Fun facts 1 · Source"
  text: string;     // normalized plain text
  bookkeeping: boolean;
}

const BOOKKEEPING_KEYS = new Set(['definition_confidence', 'dictionary_source', 'part_of_speech']);

function isBookkeepingKey(key: string): boolean {
  const k = key.toLowerCase();
  return BOOKKEEPING_KEYS.has(k) || /^paa(_|$)/.test(k) || k.startsWith('people_also_ask');
}

/** snake_case / camelCase key to "Sentence case" label. */
function labelFor(key: string): string {
  const words = key
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_\-\s]+/g, ' ')
    .trim()
    .toLowerCase()
    .split(' ')
    .filter(Boolean)
    .map((w) => (w === 'paa' ? 'PAA' : w));
  if (words.length === 0) return key;
  const first = words[0] === 'PAA' ? 'PAA' : words[0].charAt(0).toUpperCase() + words[0].slice(1);
  return [first, ...words.slice(1)].join(' ');
}

/** Plain text with markdown emphasis and all whitespace differences removed. */
function cleanText(s: string): string {
  return s
    .replace(/\*\*|__|`/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

function stripFences(raw: string): string {
  const t = raw.trim();
  const fenced = t.match(/^```[a-zA-Z]*\s*([\s\S]*?)\s*```$/);
  return fenced ? fenced[1] : t;
}

/**
 * Close a JSON reply that stopped early (output token cap): cut back to the last
 * complete value before a comma, or keep all of it, and add the missing closers.
 * Tries the latest cut first. Returns undefined when nothing can be recovered.
 */
function repairTruncated(body: string): unknown {
  const cuts: { at: number; closers: string }[] = [];
  const stack: string[] = [];
  let inString = false;
  let escaped = false;
  for (let i = 0; i < body.length; i++) {
    const c = body[i];
    if (inString) {
      if (escaped) escaped = false;
      else if (c === '\\') escaped = true;
      else if (c === '"') inString = false;
      continue;
    }
    if (c === '"') inString = true;
    else if (c === '{') stack.push('}');
    else if (c === '[') stack.push(']');
    else if (c === '}' || c === ']') stack.pop();
    else if (c === ',' && stack.length) cuts.push({ at: i, closers: stack.slice().reverse().join('') });
  }
  if (!inString && stack.length) cuts.push({ at: body.length, closers: stack.slice().reverse().join('') });
  for (let k = cuts.length - 1; k >= 0 && k >= cuts.length - 50; k--) {
    try { return JSON.parse(body.slice(0, cuts[k].at) + cuts[k].closers); } catch { /* try an earlier cut */ }
  }
  return undefined;
}

interface Parsed {
  value: unknown;      // undefined when the reply is not JSON at all
  malformed: boolean;  // reply looked like JSON but was cut off or broken
}

function parseReply(raw: string): Parsed {
  const body = stripFences(raw);
  try {
    const value = JSON.parse(body);
    // A JSON object stored as a JSON string: parse the inner text once.
    if (typeof value === 'string' && /^\s*[[{]/.test(value)) return parseReply(value);
    return { value, malformed: false };
  } catch {
    // Some replies wrap the JSON in a sentence; take the outermost object or array.
    const start = body.search(/[[{]/);
    const end = Math.max(body.lastIndexOf('}'), body.lastIndexOf(']'));
    if (start >= 0 && end > start) {
      try { return { value: JSON.parse(body.slice(start, end + 1)), malformed: false }; } catch { /* fall through */ }
    }
    // Starts like JSON but does not parse: recover what it can, flag it.
    if (start >= 0 && /^\s*[[{]/.test(body.slice(0, start + 1))) {
      return { value: repairTruncated(body.slice(start)), malformed: true };
    }
    return { value: undefined, malformed: false };
  }
}

function scalarText(v: unknown): string {
  if (v === null || v === undefined) return '';
  if (typeof v === 'string') return cleanText(v);
  return String(v);
}

function isScalar(v: unknown): boolean {
  return v === null || v === undefined || typeof v !== 'object';
}

function walk(value: unknown, path: string, label: string, hidden: boolean, out: OutputField[]): void {
  if (isScalar(value)) {
    out.push({ key: path, label, text: scalarText(value), bookkeeping: hidden });
    return;
  }
  if (Array.isArray(value)) {
    if (value.every(isScalar)) {
      const lines = value.map(scalarText).filter(Boolean);
      const text = lines.length <= 1 ? (lines[0] ?? '') : lines.map((l, i) => `${i + 1}. ${l}`).join('\n');
      out.push({ key: path, label, text, bookkeeping: hidden });
      return;
    }
    value.forEach((item, i) => {
      walk(item, `${path}.${i + 1}`, `${label} ${i + 1}`, hidden, out);
    });
    return;
  }
  for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
    const childPath = path ? `${path}.${k}` : k;
    const childLabel = label ? `${label} · ${labelFor(k)}` : labelFor(k);
    walk(v, childPath, childLabel, hidden || isBookkeepingKey(k), out);
  }
}

/** True when the reply looked like JSON but was cut off or malformed. */
export function isMalformedReply(raw: string | null | undefined): boolean {
  return parseReply(raw ?? '').malformed;
}

/**
 * Raw model reply to ordered fields. A JSON reply (or a JSON string holding one)
 * becomes labeled fields; a cut-off JSON reply gives the fields it can recover
 * and never its raw text. Only a plain prose reply becomes one "Output" field.
 */
export function outputToFields(raw: string | null | undefined): OutputField[] {
  const text = raw ?? '';
  const { value: parsed, malformed } = parseReply(text);
  if (parsed === undefined || isScalar(parsed)) {
    if (malformed) return [];
    return [{ key: 'output', label: 'Output', text: cleanText(isScalar(parsed) && parsed !== undefined ? String(parsed) : text), bookkeeping: false }];
  }
  const out: OutputField[] = [];
  if (Array.isArray(parsed) && !parsed.every(isScalar)) {
    parsed.forEach((item, i) => walk(item, `item.${i + 1}`, `Item ${i + 1}`, false, out));
  } else {
    walk(parsed, '', Array.isArray(parsed) ? 'Items' : '', false, out);
  }
  if (out.length) return out;
  return malformed ? [] : [{ key: 'output', label: 'Output', text: '', bookkeeping: false }];
}

/**
 * Line up several outputs for the same word: every card gets the same field list in
 * the same order (union of all keys, first seen wins), with a blank for a field an
 * output left out. Key order in any one reply therefore never shows on screen.
 */
export function alignFields(lists: OutputField[][]): OutputField[][] {
  const order: string[] = [];
  const meta = new Map<string, { label: string; bookkeeping: boolean }>();
  for (const list of lists) {
    for (const f of list) {
      if (!meta.has(f.key)) {
        order.push(f.key);
        meta.set(f.key, { label: f.label, bookkeeping: f.bookkeeping });
      }
    }
  }
  return lists.map((list) => {
    const byKey = new Map(list.map((f) => [f.key, f]));
    return order.map((key) => {
      const m = meta.get(key)!;
      return { key, label: m.label, bookkeeping: m.bookkeeping, text: byKey.get(key)?.text ?? '' };
    });
  });
}
